/**
 * Social Media Management Dashboard
 * ===================================
 * Features:
 * - Multi-platform social media posting
 * - AI-powered caption and hashtag generation
 * - Post scheduling with calendar view
 * - Analytics dashboard
 * - Platform connection management (On/Off toggle)
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform Icons SVG Components
const PlatformIcons = {
  twitter: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  facebook: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  instagram: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  linkedin: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  pinterest: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
    </svg>
  ),
  youtube: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  tiktok: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  ),
  telegram: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  ),
  whatsapp: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  ),
  threads: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.024.921-.767 2.205-1.199 3.611-1.215 1.028-.011 1.968.145 2.798.457.012-.622-.005-1.205-.055-1.746-.075-.81-.245-1.405-.53-1.875-.404-.666-.987-1.133-1.734-1.39-.768-.263-1.725-.387-2.842-.368-.871.014-1.625.14-2.24.375-.772.294-1.355.742-1.733 1.33-.314.485-.51 1.083-.6 1.83l-2.02-.278c.12-.976.393-1.808.832-2.543.552-.923 1.378-1.636 2.456-2.12.888-.399 1.902-.62 3.013-.66 1.429-.054 2.637.096 3.59.446 1.166.428 2.058 1.123 2.65 2.065.458.73.742 1.612.867 2.7.044.387.066.8.066 1.237v.296c.872.457 1.596 1.071 2.132 1.82.717 1.001 1.074 2.197 1.061 3.556-.015 1.506-.473 2.885-1.363 4.098-1.02 1.389-2.473 2.378-4.32 2.94-1.187.361-2.532.546-3.996.546h-.003zM10.197 14.97c-.592.048-1.097.18-1.503.395-.466.247-.773.591-.889 1.003-.092.327-.058.646.098.923.202.358.596.648 1.112.819.37.122.79.182 1.252.18.844-.019 1.58-.278 2.067-.727.406-.374.62-.877.6-1.418-.014-.384-.106-.725-.273-1.015-.296-.51-.835-.89-1.606-1.131-.356-.11-.747-.044-.858.971z"/>
    </svg>
  ),
  google: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
    </svg>
  ),
  snapchat: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.017.003c1.555.005 2.86.205 3.938.63 1.672.66 2.897 1.697 3.639 3.084.524.98.787 2.058.887 3.264.032.39.04.98.04 1.73-.004.25-.004.5-.004.76.15.05.32.07.5.07.4 0 .72-.11.95-.26.24-.16.41-.28.59-.28.14 0 .27.05.41.14.13.09.25.21.33.35.22.37.06.84-.35 1.05-.22.11-.44.19-.66.26-.24.08-.51.12-.74.16-.24.04-.45.08-.58.12-.12.03-.22.06-.3.11-.09.05-.16.12-.22.21-.05.08-.09.17-.12.28-.03.1-.05.22-.07.35 0 .1-.02.21-.02.34.01.17.04.36.09.56.14.54.44 1.14.89 1.73.47.61 1.08 1.19 1.83 1.66.34.21.7.4 1.09.55.39.15.81.26 1.23.32.16.02.32.04.47.04.14.01.27 0 .4-.02.08-.01.15-.03.21-.05.06-.02.12-.05.16-.08.14-.11.18-.28.14-.44-.03-.12-.11-.23-.2-.31-.24-.21-.56-.35-.89-.46-.33-.11-.67-.19-1-.29-.17-.05-.33-.11-.48-.17-.17-.07-.32-.15-.46-.24-.47-.3-.77-.74-.93-1.24-.16-.5-.19-1.05-.19-1.57.01-.16.03-.31.06-.45.16.01.32.02.49.02.36 0 .73-.04 1.08-.12.35-.08.69-.2 1-.35.31-.14.6-.31.85-.5.25-.2.47-.41.66-.65.19-.24.34-.5.45-.78.11-.28.18-.58.21-.89.01-.1.02-.2.02-.3v-.11c.01-.14-.01-.28-.04-.42-.06-.26-.18-.5-.35-.71-.17-.21-.38-.39-.62-.51-.25-.13-.52-.2-.8-.2-.14 0-.28.01-.41.04-.14.03-.27.07-.4.12-.05.02-.1.04-.14.06-.1-.66-.27-1.29-.51-1.87-.45-1.07-1.12-1.98-1.97-2.7-.85-.72-1.86-1.25-3-1.57-1.14-.32-2.39-.43-3.74-.35-1.68.1-3.14.56-4.37 1.35-1.23.79-2.22 1.88-2.94 3.18-.36.65-.63 1.35-.81 2.08-.19.73-.29 1.5-.32 2.28-.04.96.04 1.9.23 2.81.03.15.07.3.1.44h-.04c-.3-.05-.62-.07-.93-.07-.28 0-.55.02-.81.06-.26.04-.5.1-.74.18-.23.08-.45.17-.65.29-.2.11-.38.24-.54.39-.16.15-.3.31-.41.49-.11.18-.2.37-.26.58-.06.2-.09.42-.09.64 0 .2.03.4.08.58.11.38.32.72.59 1 .28.28.61.49.97.62.37.14.76.2 1.14.2.18 0 .36-.01.53-.04v.05c-.02.23-.01.47.04.71.04.22.12.43.23.62.11.2.25.38.41.53.16.16.35.29.55.4.2.11.42.19.64.25.22.06.45.1.68.12.23.02.46.02.69 0-.05.13-.08.27-.11.41-.05.25-.08.51-.08.77v.35c0 .23.02.46.06.68.08.44.24.85.49 1.22.24.36.57.68.96.92.78.49 1.76.74 2.88.74.51 0 1.04-.05 1.58-.15.54-.1 1.08-.26 1.6-.46.53-.2 1.03-.45 1.5-.75.47-.3.9-.64 1.28-1.03.38-.38.71-.8.98-1.26.27-.45.48-.94.62-1.45.14-.51.2-1.04.2-1.58 0-.27-.02-.53-.05-.8-.07-.52-.2-1.03-.38-1.51-.19-.49-.43-.96-.73-1.38-.29-.43-.64-.81-1.03-1.14-.39-.33-.82-.61-1.28-.83-.46-.22-.95-.38-1.45-.48-.51-.1-1.03-.14-1.54-.13zM12 3.95c.85 0 1.68.11 2.46.34 1.17.34 2.15.91 2.91 1.69.76.78 1.3 1.74 1.59 2.84.15.55.24 1.13.26 1.72.02.58-.02 1.17-.11 1.75-.1.58-.25 1.14-.46 1.67-.21.53-.48 1.03-.8 1.49-.32.46-.69.88-1.11 1.24-.42.37-.88.68-1.39.93-.51.25-1.05.43-1.62.55-.57.12-1.17.17-1.79.17-.62 0-1.22-.05-1.79-.17-.57-.12-1.11-.3-1.62-.55-.51-.25-.97-.57-1.39-.93-.42-.37-.79-.78-1.11-1.24-.32-.46-.59-.96-.8-1.49-.21-.53-.36-1.09-.46-1.67-.09-.58-.13-1.17-.11-1.75.02-.59.11-1.17.26-1.72.29-1.1.83-2.06 1.59-2.84.76-.78 1.74-1.35 2.91-1.69.78-.23 1.61-.34 2.46-.34h.11z"/>
    </svg>
  ),
  wordpress: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M21.469 6.825c.84 1.537 1.318 3.3 1.318 5.175 0 3.979-2.156 7.456-5.363 9.325l3.295-9.527c.615-1.54.82-2.771.82-3.864 0-.405-.026-.78-.07-1.11m-7.981.105c.647-.034 1.232-.105 1.232-.105.582-.07.514-.925-.068-.892 0 0-1.749.138-2.877.138-.998 0-2.681-.138-2.681-.138-.582-.033-.664.858-.082.892 0 0 .548.07 1.127.105l1.674 4.58-2.35 7.05-3.911-11.63c.646-.034 1.23-.105 1.23-.105.582-.07.514-.925-.067-.892 0 0-1.749.138-2.878.138-.202 0-.44-.005-.695-.014C3.396 2.921 7.375.5 12 .5c3.446 0 6.583 1.32 8.935 3.479-.058-.004-.114-.013-.173-.013-1.062 0-1.815.92-1.815 1.914 0 .892.514 1.646 1.062 2.536.411.72.89 1.64.89 2.973 0 .924-.356 1.994-.823 3.487l-1.08 3.608-3.908-11.634zM12 21.5c-1.326 0-2.598-.22-3.787-.62l4.02-11.673 4.117 11.28c.028.066.06.128.095.187A9.455 9.455 0 0112 21.5M.5 12C.5 8.965 1.64 6.19 3.508 4.14l5.22 14.312C4.218 16.934 .5 14.86.5 12m11.5-12C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0"/>
    </svg>
  ),
  medium: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zm7.42 0c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/>
    </svg>
  ),
  tumblr: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M14.563 24c-5.093 0-7.031-3.756-7.031-6.411V9.747H5.116V6.648c3.63-1.313 4.512-4.596 4.71-6.469C9.84.051 9.941 0 9.999 0h3.517v6.114h4.801v3.633h-4.82v7.47c.016 1.001.375 2.371 2.207 2.371h.09c.631-.02 1.486-.205 1.936-.419l1.156 3.425c-.436.636-2.4 1.374-4.156 1.404h-.168z"/>
    </svg>
  ),
  reddit: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
    </svg>
  ),
  vimeo: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.9765 6.4168c-.105 2.338-1.739 5.5429-4.894 9.6088-3.2679 4.247-6.0258 6.3699-8.2898 6.3699-1.409 0-2.578-1.294-3.553-3.881l-1.9179-7.1138c-.719-2.584-1.488-3.878-2.312-3.878-.179 0-.806.378-1.8809 1.132l-1.129-1.457a315.06 315.06 0 003.501-3.1279c1.579-1.368 2.765-2.085 3.5539-2.159 1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.5069.5382 2.45 1.1309 3.674 1.7759 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.8679 3.434-5.7568 6.7619-5.6368 2.4729.06 3.6279 1.664 3.4929 4.7969z"/>
    </svg>
  ),
  shopify: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.021-.118-.125-.221-.246-.226s-2.558-.184-2.558-.184-.946-.946-1.307-1.303c-.095-.094-.218-.146-.346-.151l-.921 21.155zm-3.411-7.818c-.344-.183-1.049-.404-1.8-.404-1.493 0-1.564.938-1.564 1.172 0 1.287 3.367 1.779 3.367 4.793 0 2.371-1.502 3.897-3.528 3.897-2.432 0-3.674-1.511-3.674-1.511l.649-2.148s1.277 1.098 2.355 1.098c.703 0 .99-.553.99-.96 0-1.68-2.762-1.756-2.762-4.512 0-2.318 1.665-4.564 5.028-4.564 1.294 0 1.933.371 1.933.371l-.994 2.768zM14.304.489c.128.008.246.018.388.034-.024-.092-.052-.18-.082-.256-.408-1.033-1.127-1.574-1.95-1.574-.569 0-.569.725-.569.725-.049 5.077 3.453 4.945 3.453 4.945V.489h-1.24zM11.56.207c-.024-.08-.058-.152-.096-.217-.392-.667-.963-.867-1.511-.867-.547 0-.836.374-.836.374-.032 3.377 2.309 3.427 2.443 3.427V.207z"/>
    </svg>
  ),
  woocommerce: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M2.227 4.857A2.228 2.228 0 000 7.094v7.457c0 1.236 1.001 2.237 2.237 2.237h3.09l-.006 3.07 3.746-3.07h12.696c1.236 0 2.237-1 2.237-2.237V7.094c0-1.236-1-2.237-2.237-2.237zm2.067 2.57c.606 0 1.098.42 1.098 1.18 0 1.678-.627 3.263-1.359 4.088-.183.206-.429.32-.691.32a.93.93 0 01-.742-.37c-.18-.227-.24-.524-.167-.82.327-1.305.427-2.087.465-2.71.051-.833.395-1.688 1.396-1.688zm5.086 0c.606 0 1.098.42 1.098 1.18 0 1.678-.627 3.263-1.359 4.088-.183.206-.43.32-.691.32a.93.93 0 01-.742-.37c-.18-.227-.24-.524-.167-.82.327-1.305.427-2.087.465-2.71.051-.833.394-1.688 1.396-1.688zm4.847.07c.903 0 1.604.544 1.907 1.512.192.613.227 1.293.097 1.903-.249 1.168-.986 1.941-1.882 1.941-.286 0-.56-.079-.79-.23a1.376 1.376 0 01-.538-.668c-.223-.553-.215-1.205.023-1.834.368-.975 1.053-1.598 1.777-1.621.131-.003.266-.003.406-.003z"/>
    </svg>
  ),
  rss: () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M6.503 20.752c0 1.794-1.456 3.248-3.251 3.248-1.796 0-3.252-1.454-3.252-3.248 0-1.794 1.456-3.248 3.252-3.248 1.795.001 3.251 1.454 3.251 3.248zm-6.503-12.572v4.811c6.05.062 10.96 4.966 11.022 11.009h4.817c-.062-8.71-7.118-15.758-15.839-15.82zm0-8.18v4.819c12.352.062 22.355 10.056 22.417 22.18h4.817C22.172 9.883 14.115 1.826 0 0z"/>
    </svg>
  )
};

// Tab Button Component
const TabButton = ({ active, onClick, children, icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-lg transition-all ${
      active
        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
    }`}
    data-testid={`tab-${children.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {icon}
    {children}
  </button>
);

// Platform Toggle Card
const PlatformCard = ({ platform, onToggle, onConnect, onDisconnect }) => {
  const IconComponent = PlatformIcons[platform.platform];
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all"
      data-testid={`platform-card-${platform.platform}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center text-white"
            style={{ backgroundColor: platform.color }}
          >
            {IconComponent && <IconComponent />}
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{platform.name}</h3>
            <p className="text-sm text-gray-500">{platform.name_hi}</p>
            {platform.account_name && (
              <p className="text-xs text-green-600">@{platform.account_name}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {platform.is_connected ? (
            <>
              {/* Toggle Switch */}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={platform.is_enabled}
                  onChange={(e) => onToggle(platform.platform, e.target.checked)}
                  className="sr-only peer"
                  data-testid={`toggle-${platform.platform}`}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
              <button
                onClick={() => onDisconnect(platform.platform)}
                className="text-red-500 hover:text-red-600 text-sm"
                data-testid={`disconnect-${platform.platform}`}
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={() => onConnect(platform.platform)}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm rounded-lg hover:opacity-90"
              data-testid={`connect-${platform.platform}`}
            >
              Connect
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Post Composer Component
const PostComposer = ({ platforms, onPost, schoolId }) => {
  const { t } = useLanguage();
  const [content, setContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [mediaType, setMediaType] = useState('none');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isDraft, setIsDraft] = useState(false);
  const [isGeneratingCaption, setIsGeneratingCaption] = useState(false);
  const [isGeneratingHashtags, setIsGeneratingHashtags] = useState(false);
  const [captionTopic, setCaptionTopic] = useState('');
  const [captionTone, setCaptionTone] = useState('professional');
  const [captionLanguage, setCaptionLanguage] = useState('hindi');
  const [showAIPanel, setShowAIPanel] = useState(false);
  
  const enabledPlatforms = platforms.filter(p => p.is_connected && p.is_enabled);
  
  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };
  
  const addHashtag = () => {
    if (hashtagInput.trim()) {
      const tag = hashtagInput.startsWith('#') ? hashtagInput : `#${hashtagInput}`;
      if (!hashtags.includes(tag)) {
        setHashtags([...hashtags, tag]);
      }
      setHashtagInput('');
    }
  };
  
  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter(h => h !== tag));
  };
  
  const generateCaption = async () => {
    if (!captionTopic.trim()) return;
    setIsGeneratingCaption(true);
    
    try {
      const res = await fetch(`${API_URL}/api/social-media/ai/generate-caption`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: captionTopic,
          platform: selectedPlatforms[0] || 'facebook',
          tone: captionTone,
          language: captionLanguage,
          include_hashtags: true,
          include_emoji: true
        })
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.caption);
      }
    } catch (error) {
      console.error('Caption generation error:', error);
    }
    setIsGeneratingCaption(false);
  };
  
  const generateHashtags = async () => {
    if (!captionTopic.trim()) return;
    setIsGeneratingHashtags(true);
    
    try {
      const res = await fetch(`${API_URL}/api/social-media/ai/generate-hashtags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: captionTopic,
          platform: selectedPlatforms[0] || 'instagram',
          count: 10,
          language: captionLanguage
        })
      });
      const data = await res.json();
      if (data.success) {
        setHashtags([...hashtags, ...data.hashtags.filter(h => !hashtags.includes(h))]);
      }
    } catch (error) {
      console.error('Hashtag generation error:', error);
    }
    setIsGeneratingHashtags(false);
  };
  
  const handleSubmit = async () => {
    if (!content.trim() || selectedPlatforms.length === 0) return;
    
    const postData = {
      content: content + (hashtags.length > 0 ? '\n\n' + hashtags.join(' ') : ''),
      platforms: selectedPlatforms,
      media_urls: mediaUrls,
      media_type: mediaType,
      hashtags: hashtags,
      scheduled_time: scheduledTime ? new Date(scheduledTime).toISOString() : null,
      is_draft: isDraft
    };
    
    await onPost(postData);
    
    // Reset form
    setContent('');
    setSelectedPlatforms([]);
    setHashtags([]);
    setMediaUrls([]);
    setScheduledTime('');
    setIsDraft(false);
  };
  
  const charCount = content.length;
  const minMaxChars = selectedPlatforms.length > 0 
    ? Math.min(...selectedPlatforms.map(p => platforms.find(pl => pl.platform === p)?.max_chars || 5000))
    : 5000;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="post-composer">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {t('नई पोस्ट बनाएं', 'Create New Post')}
      </h3>
      
      {/* Platform Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('प्लेटफॉर्म चुनें', 'Select Platforms')}
        </label>
        <div className="flex flex-wrap gap-2">
          {enabledPlatforms.map(platform => {
            const IconComponent = PlatformIcons[platform.platform];
            return (
              <button
                key={platform.platform}
                onClick={() => togglePlatform(platform.platform)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                  selectedPlatforms.includes(platform.platform)
                    ? 'text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={selectedPlatforms.includes(platform.platform) ? { backgroundColor: platform.color } : {}}
                data-testid={`select-platform-${platform.platform}`}
              >
                {IconComponent && <IconComponent />}
                {platform.name}
              </button>
            );
          })}
          {enabledPlatforms.length === 0 && (
            <p className="text-gray-500 text-sm">
              {t('कोई प्लेटफॉर्म कनेक्ट नहीं है। पहले प्लेटफॉर्म कनेक्ट करें।', 
                 'No platforms connected. Please connect a platform first.')}
            </p>
          )}
        </div>
      </div>
      
      {/* AI Content Generation Panel */}
      <div className="mb-4">
        <button
          onClick={() => setShowAIPanel(!showAIPanel)}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
          data-testid="toggle-ai-panel"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          {t('AI से कंटेंट बनाएं', 'Generate with AI')}
          <svg className={`w-4 h-4 transition-transform ${showAIPanel ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showAIPanel && (
          <div className="mt-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('विषय', 'Topic')}
                </label>
                <input
                  type="text"
                  value={captionTopic}
                  onChange={(e) => setCaptionTopic(e.target.value)}
                  placeholder={t('जैसे: वार्षिक खेल दिवस', 'e.g., Annual Sports Day')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="ai-topic-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('टोन', 'Tone')}
                </label>
                <select
                  value={captionTone}
                  onChange={(e) => setCaptionTone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="ai-tone-select"
                >
                  <option value="professional">{t('पेशेवर', 'Professional')}</option>
                  <option value="casual">{t('आरामदेह', 'Casual')}</option>
                  <option value="engaging">{t('आकर्षक', 'Engaging')}</option>
                  <option value="educational">{t('शैक्षिक', 'Educational')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('भाषा', 'Language')}
                </label>
                <select
                  value={captionLanguage}
                  onChange={(e) => setCaptionLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="ai-language-select"
                >
                  <option value="hindi">{t('हिंदी', 'Hindi')}</option>
                  <option value="english">{t('अंग्रेजी', 'English')}</option>
                  <option value="hinglish">{t('हिंगलिश', 'Hinglish')}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={generateCaption}
                disabled={isGeneratingCaption || !captionTopic.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
                data-testid="generate-caption-btn"
              >
                {isGeneratingCaption ? (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                )}
                {t('कैप्शन बनाएं', 'Generate Caption')}
              </button>
              <button
                onClick={generateHashtags}
                disabled={isGeneratingHashtags || !captionTopic.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg text-sm hover:bg-pink-700 disabled:opacity-50"
                data-testid="generate-hashtags-btn"
              >
                {isGeneratingHashtags ? (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <span>#</span>
                )}
                {t('हैशटैग बनाएं', 'Generate Hashtags')}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Content Textarea */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('पोस्ट कंटेंट', 'Post Content')}
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          placeholder={t('अपनी पोस्ट यहां लिखें...', 'Write your post here...')}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          data-testid="post-content-input"
        />
        <div className="flex justify-between mt-1">
          <span className={`text-sm ${charCount > minMaxChars ? 'text-red-500' : 'text-gray-500'}`}>
            {charCount} / {minMaxChars} {t('अक्षर', 'characters')}
          </span>
        </div>
      </div>
      
      {/* Hashtags */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('हैशटैग', 'Hashtags')}
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {hashtags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
            >
              {tag}
              <button
                onClick={() => removeHashtag(tag)}
                className="text-purple-500 hover:text-purple-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={hashtagInput}
            onChange={(e) => setHashtagInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
            placeholder="#hashtag"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="hashtag-input"
          />
          <button
            onClick={addHashtag}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
            data-testid="add-hashtag-btn"
          >
            {t('जोड़ें', 'Add')}
          </button>
        </div>
      </div>
      
      {/* Schedule */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('शेड्यूल (वैकल्पिक)', 'Schedule (Optional)')}
        </label>
        <input
          type="datetime-local"
          value={scheduledTime}
          onChange={(e) => setScheduledTime(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          data-testid="schedule-input"
        />
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => { setIsDraft(true); handleSubmit(); }}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
          data-testid="save-draft-btn"
        >
          {t('ड्राफ्ट सहेजें', 'Save Draft')}
        </button>
        <button
          onClick={() => { setIsDraft(false); handleSubmit(); }}
          disabled={!content.trim() || selectedPlatforms.length === 0}
          className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
          data-testid="publish-post-btn"
        >
          {scheduledTime 
            ? t('शेड्यूल करें', 'Schedule') 
            : t('अभी पोस्ट करें', 'Post Now')}
        </button>
      </div>
    </div>
  );
};

// Posts List Component
const PostsList = ({ posts, onPublish, onDelete }) => {
  const { t } = useLanguage();
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { text: t('प्रकाशित', 'Published'), color: 'bg-green-100 text-green-700' },
      scheduled: { text: t('शेड्यूल्ड', 'Scheduled'), color: 'bg-blue-100 text-blue-700' },
      draft: { text: t('ड्राफ्ट', 'Draft'), color: 'bg-gray-100 text-gray-700' },
      pending: { text: t('लंबित', 'Pending'), color: 'bg-yellow-100 text-yellow-700' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };
  
  if (posts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
        <p className="text-gray-500">
          {t('अभी कोई पोस्ट नहीं है।', 'No posts yet.')}
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {posts.map(post => (
        <div 
          key={post.id} 
          className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all"
          data-testid={`post-item-${post.id}`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {post.platforms?.map(platform => {
                const IconComponent = PlatformIcons[platform];
                return IconComponent ? (
                  <span key={platform} className="text-gray-400">
                    <IconComponent />
                  </span>
                ) : null;
              })}
              {getStatusBadge(post.status)}
            </div>
            <div className="text-sm text-gray-500">
              {post.created_at && new Date(post.created_at).toLocaleDateString('hi-IN')}
            </div>
          </div>
          
          <p className="text-gray-700 mb-3 line-clamp-3">{post.content}</p>
          
          {post.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {post.hashtags.slice(0, 5).map((tag, i) => (
                <span key={i} className="text-xs text-purple-600">{tag}</span>
              ))}
              {post.hashtags.length > 5 && (
                <span className="text-xs text-gray-400">+{post.hashtags.length - 5}</span>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {post.scheduled_time && (
                <span>{t('शेड्यूल्ड:', 'Scheduled:')} {new Date(post.scheduled_time).toLocaleString('hi-IN')}</span>
              )}
            </div>
            <div className="flex gap-2">
              {post.status !== 'published' && (
                <button
                  onClick={() => onPublish(post.id)}
                  className="px-3 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded-lg"
                  data-testid={`publish-btn-${post.id}`}
                >
                  {t('प्रकाशित करें', 'Publish')}
                </button>
              )}
              <button
                onClick={() => onDelete(post.id)}
                className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                data-testid={`delete-btn-${post.id}`}
              >
                {t('हटाएं', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Analytics Overview Component
const AnalyticsOverview = ({ analytics }) => {
  const { t } = useLanguage();
  
  const stats = [
    { label: t('कुल पोस्ट', 'Total Posts'), value: analytics?.total_posts || 0, color: 'bg-purple-100 text-purple-700' },
    { label: t('शेड्यूल्ड', 'Scheduled'), value: analytics?.scheduled_posts || 0, color: 'bg-blue-100 text-blue-700' },
    { label: t('ड्राफ्ट', 'Drafts'), value: analytics?.draft_posts || 0, color: 'bg-gray-100 text-gray-700' }
  ];
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="analytics-overview">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {t('विश्लेषण सारांश', 'Analytics Overview')}
      </h3>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {stats.map((stat, index) => (
          <div key={index} className={`p-4 rounded-lg ${stat.color}`}>
            <p className="text-2xl font-bold">{stat.value}</p>
            <p className="text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
      
      {analytics?.platform_breakdown && Object.keys(analytics.platform_breakdown).length > 0 && (
        <div>
          <h4 className="font-medium text-gray-700 mb-3">
            {t('प्लेटफॉर्म वार', 'By Platform')}
          </h4>
          <div className="space-y-2">
            {Object.entries(analytics.platform_breakdown).map(([platform, data]) => {
              const IconComponent = PlatformIcons[platform];
              return (
                <div key={platform} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {IconComponent && <span className="text-gray-400"><IconComponent /></span>}
                    <span className="font-medium capitalize">{platform}</span>
                  </div>
                  <span className="text-gray-600">{data.posts} {t('पोस्ट', 'posts')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Templates Component
const TemplatesPanel = ({ templates, onUseTemplate }) => {
  const { t, language } = useLanguage();
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="templates-panel">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {t('पोस्ट टेम्पलेट', 'Post Templates')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map(template => (
          <div 
            key={template.id}
            className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 cursor-pointer transition-all"
            onClick={() => onUseTemplate(template)}
            data-testid={`template-${template.id}`}
          >
            <h4 className="font-medium text-gray-800">
              {language === 'hi' ? template.name_hi : template.name}
            </h4>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {language === 'hi' ? template.content_hi : template.content_en}
            </p>
            <div className="flex gap-1 mt-2">
              {template.suggested_platforms?.map(platform => {
                const IconComponent = PlatformIcons[platform];
                return IconComponent ? (
                  <span key={platform} className="text-gray-300 w-4 h-4">
                    <IconComponent />
                  </span>
                ) : null;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Connection Modal
const ConnectionModal = ({ isOpen, onClose, platform, onSubmit }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    accountName: '',
    apiKey: '',
    apiSecret: '',
    accessToken: '',
    accessTokenSecret: '',
    pageId: '',
    appId: '',
    appSecret: '',
    clientId: '',
    clientSecret: '',
    botToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    siteUrl: '',
    username: '',
    password: '',
    storeUrl: '',
    subreddit: ''
  });
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      platform,
      account_name: formData.accountName || null,
      api_key: formData.apiKey || formData.appId || formData.clientId || null,
      api_secret: formData.apiSecret || formData.appSecret || formData.clientSecret || null,
      access_token: formData.accessToken || formData.botToken || null,
      access_token_secret: formData.accessTokenSecret || null,
      page_id: formData.pageId || null,
      phone_number_id: formData.phoneNumberId || null,
      business_account_id: formData.businessAccountId || null,
      site_url: formData.siteUrl || formData.storeUrl || null,
      username: formData.username || null,
      password: formData.password || null,
      subreddit: formData.subreddit || null
    });
    onClose();
    // Reset form
    setFormData({
      accountName: '', apiKey: '', apiSecret: '', accessToken: '', accessTokenSecret: '',
      pageId: '', appId: '', appSecret: '', clientId: '', clientSecret: '', botToken: '',
      phoneNumberId: '', businessAccountId: '', siteUrl: '', username: '', password: '',
      storeUrl: '', subreddit: ''
    });
  };
  
  if (!isOpen) return null;
  
  // Platform-specific credential requirements
  const platformCredentials = {
    twitter: {
      name: 'Twitter / X',
      name_hi: 'ट्विटर / एक्स',
      fields: [
        { key: 'apiKey', label: 'API Key (Consumer Key)', labelHi: 'API Key', required: true },
        { key: 'apiSecret', label: 'API Secret (Consumer Secret)', labelHi: 'API Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Access Token', labelHi: 'Access Token', required: true },
        { key: 'accessTokenSecret', label: 'Access Token Secret', labelHi: 'Access Token Secret', required: true, type: 'password' }
      ],
      helpUrl: 'https://developer.twitter.com/en/portal/dashboard',
      helpText: 'Twitter Developer Portal से credentials प्राप्त करें'
    },
    facebook: {
      name: 'Facebook',
      name_hi: 'फेसबुक',
      fields: [
        { key: 'appId', label: 'App ID', labelHi: 'App ID', required: true },
        { key: 'appSecret', label: 'App Secret', labelHi: 'App Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Page Access Token', labelHi: 'Page Access Token', required: true, type: 'password' },
        { key: 'pageId', label: 'Page ID', labelHi: 'Page ID', required: true }
      ],
      helpUrl: 'https://developers.facebook.com/apps/',
      helpText: 'Meta for Developers से App बनाएं और Page Access Token generate करें'
    },
    instagram: {
      name: 'Instagram',
      name_hi: 'इंस्टाग्राम',
      fields: [
        { key: 'appId', label: 'Facebook App ID', labelHi: 'Facebook App ID', required: true },
        { key: 'appSecret', label: 'Facebook App Secret', labelHi: 'App Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Instagram Access Token', labelHi: 'Access Token', required: true, type: 'password' },
        { key: 'pageId', label: 'Instagram Business Account ID', labelHi: 'Business Account ID', required: true }
      ],
      helpUrl: 'https://developers.facebook.com/docs/instagram-api/',
      helpText: 'Instagram API के लिए Facebook Business Account आवश्यक है'
    },
    linkedin: {
      name: 'LinkedIn',
      name_hi: 'लिंक्डइन',
      fields: [
        { key: 'clientId', label: 'Client ID', labelHi: 'Client ID', required: true },
        { key: 'clientSecret', label: 'Client Secret', labelHi: 'Client Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Access Token', labelHi: 'Access Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://www.linkedin.com/developers/apps',
      helpText: 'LinkedIn Developer Portal से App बनाएं'
    },
    pinterest: {
      name: 'Pinterest',
      name_hi: 'पिनटरेस्ट',
      fields: [
        { key: 'appId', label: 'App ID', labelHi: 'App ID', required: true },
        { key: 'appSecret', label: 'App Secret', labelHi: 'App Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Access Token', labelHi: 'Access Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://developers.pinterest.com/apps/',
      helpText: 'Pinterest for Developers से credentials प्राप्त करें'
    },
    youtube: {
      name: 'YouTube',
      name_hi: 'यूट्यूब',
      fields: [
        { key: 'apiKey', label: 'API Key', labelHi: 'API Key', required: true },
        { key: 'clientId', label: 'OAuth Client ID', labelHi: 'Client ID', required: true },
        { key: 'clientSecret', label: 'OAuth Client Secret', labelHi: 'Client Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Refresh Token', labelHi: 'Refresh Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://console.cloud.google.com/apis/credentials',
      helpText: 'Google Cloud Console से YouTube Data API v3 enable करें'
    },
    tiktok: {
      name: 'TikTok',
      name_hi: 'टिकटॉक',
      fields: [
        { key: 'appId', label: 'App ID', labelHi: 'App ID', required: true },
        { key: 'appSecret', label: 'App Secret', labelHi: 'App Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Access Token', labelHi: 'Access Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://developers.tiktok.com/',
      helpText: 'TikTok for Developers से App बनाएं'
    },
    telegram: {
      name: 'Telegram',
      name_hi: 'टेलीग्राम',
      fields: [
        { key: 'botToken', label: 'Bot Token', labelHi: 'Bot Token', required: true, type: 'password', placeholder: '123456789:ABCdefGHIjklMNO...' }
      ],
      helpUrl: 'https://t.me/BotFather',
      helpText: '@BotFather से Bot बनाएं और Token प्राप्त करें'
    },
    whatsapp_business: {
      name: 'WhatsApp Business',
      name_hi: 'व्हाट्सएप बिजनेस',
      fields: [
        { key: 'phoneNumberId', label: 'Phone Number ID', labelHi: 'Phone Number ID', required: true },
        { key: 'businessAccountId', label: 'Business Account ID', labelHi: 'Business Account ID', required: true },
        { key: 'accessToken', label: 'Permanent Access Token', labelHi: 'Access Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://business.facebook.com/latest/whatsapp_manager/',
      helpText: 'Meta Business Suite से WhatsApp Business API setup करें'
    },
    threads: {
      name: 'Threads',
      name_hi: 'थ्रेड्स',
      fields: [
        { key: 'appId', label: 'Instagram App ID', labelHi: 'App ID', required: true },
        { key: 'appSecret', label: 'Instagram App Secret', labelHi: 'App Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Threads Access Token', labelHi: 'Access Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://developers.facebook.com/docs/threads/',
      helpText: 'Threads API (Meta) से credentials प्राप्त करें'
    },
    google_business: {
      name: 'Google My Business',
      name_hi: 'गूगल माय बिजनेस',
      fields: [
        { key: 'clientId', label: 'OAuth Client ID', labelHi: 'Client ID', required: true },
        { key: 'clientSecret', label: 'OAuth Client Secret', labelHi: 'Client Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Refresh Token', labelHi: 'Refresh Token', required: true, type: 'password' },
        { key: 'pageId', label: 'Location ID', labelHi: 'Location ID', required: true }
      ],
      helpUrl: 'https://console.cloud.google.com/',
      helpText: 'Google Cloud Console से Business Profile API enable करें'
    },
    snapchat: {
      name: 'Snapchat',
      name_hi: 'स्नैपचैट',
      fields: [
        { key: 'clientId', label: 'Client ID', labelHi: 'Client ID', required: true },
        { key: 'clientSecret', label: 'Client Secret', labelHi: 'Client Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Access Token', labelHi: 'Access Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://kit.snapchat.com/portal/',
      helpText: 'Snap Kit Developer Portal से App बनाएं'
    },
    wordpress: {
      name: 'WordPress',
      name_hi: 'वर्डप्रेस',
      fields: [
        { key: 'siteUrl', label: 'Site URL', labelHi: 'साइट URL', required: true, placeholder: 'https://yoursite.com' },
        { key: 'username', label: 'Username', labelHi: 'Username', required: true },
        { key: 'password', label: 'Application Password', labelHi: 'Application Password', required: true, type: 'password' }
      ],
      helpUrl: 'https://make.wordpress.org/core/2020/11/05/application-passwords-integration-guide/',
      helpText: 'WordPress Dashboard > Users > Profile > Application Passwords से password बनाएं'
    },
    medium: {
      name: 'Medium',
      name_hi: 'मीडियम',
      fields: [
        { key: 'accessToken', label: 'Integration Token', labelHi: 'Integration Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://medium.com/me/settings/security',
      helpText: 'Medium Settings > Security > Integration tokens से token generate करें'
    },
    tumblr: {
      name: 'Tumblr',
      name_hi: 'टम्बलर',
      fields: [
        { key: 'apiKey', label: 'Consumer Key', labelHi: 'Consumer Key', required: true },
        { key: 'apiSecret', label: 'Consumer Secret', labelHi: 'Consumer Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'OAuth Token', labelHi: 'OAuth Token', required: true },
        { key: 'accessTokenSecret', label: 'OAuth Token Secret', labelHi: 'Token Secret', required: true, type: 'password' }
      ],
      helpUrl: 'https://www.tumblr.com/oauth/apps',
      helpText: 'Tumblr Apps से OAuth credentials प्राप्त करें'
    },
    reddit: {
      name: 'Reddit',
      name_hi: 'रेडिट',
      fields: [
        { key: 'clientId', label: 'Client ID', labelHi: 'Client ID', required: true },
        { key: 'clientSecret', label: 'Client Secret', labelHi: 'Client Secret', required: true, type: 'password' },
        { key: 'username', label: 'Reddit Username', labelHi: 'Username', required: true },
        { key: 'password', label: 'Reddit Password', labelHi: 'Password', required: true, type: 'password' },
        { key: 'subreddit', label: 'Subreddit (optional)', labelHi: 'Subreddit', required: false, placeholder: 'r/yoursubreddit' }
      ],
      helpUrl: 'https://www.reddit.com/prefs/apps',
      helpText: 'Reddit Apps से Script App बनाएं'
    },
    vimeo: {
      name: 'Vimeo',
      name_hi: 'विमियो',
      fields: [
        { key: 'clientId', label: 'Client ID', labelHi: 'Client ID', required: true },
        { key: 'clientSecret', label: 'Client Secret', labelHi: 'Client Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Access Token', labelHi: 'Access Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://developer.vimeo.com/apps',
      helpText: 'Vimeo Developer Portal से App बनाएं'
    },
    shopify: {
      name: 'Shopify',
      name_hi: 'शॉपिफाई',
      fields: [
        { key: 'storeUrl', label: 'Store URL', labelHi: 'Store URL', required: true, placeholder: 'yourstore.myshopify.com' },
        { key: 'apiKey', label: 'API Key', labelHi: 'API Key', required: true },
        { key: 'apiSecret', label: 'API Secret', labelHi: 'API Secret', required: true, type: 'password' },
        { key: 'accessToken', label: 'Admin API Access Token', labelHi: 'Access Token', required: true, type: 'password' }
      ],
      helpUrl: 'https://partners.shopify.com/',
      helpText: 'Shopify Partners > Apps > Create App से credentials प्राप्त करें'
    },
    woocommerce: {
      name: 'WooCommerce',
      name_hi: 'वूकॉमर्स',
      fields: [
        { key: 'siteUrl', label: 'Site URL', labelHi: 'साइट URL', required: true, placeholder: 'https://yourstore.com' },
        { key: 'apiKey', label: 'Consumer Key', labelHi: 'Consumer Key', required: true },
        { key: 'apiSecret', label: 'Consumer Secret', labelHi: 'Consumer Secret', required: true, type: 'password' }
      ],
      helpUrl: 'https://woocommerce.github.io/woocommerce-rest-api-docs/',
      helpText: 'WooCommerce > Settings > Advanced > REST API से keys generate करें'
    },
    rss: {
      name: 'RSS Feed',
      name_hi: 'आरएसएस फीड',
      fields: [
        { key: 'siteUrl', label: 'RSS Feed URL', labelHi: 'RSS Feed URL', required: true, placeholder: 'https://yoursite.com/feed' }
      ],
      helpUrl: '',
      helpText: 'अपने blog/website का RSS feed URL डालें'
    }
  };
  
  const info = platformCredentials[platform] || { 
    name: platform, 
    name_hi: platform,
    fields: [{ key: 'accessToken', label: 'Access Token', labelHi: 'Access Token', required: true, type: 'password' }],
    helpText: ''
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg my-8">
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-purple-600 to-pink-600 rounded-t-xl">
          <h3 className="text-lg font-semibold text-white">
            {t('कनेक्ट करें', 'Connect')} {t(info.name_hi, info.name)}
          </h3>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Account Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('अकाउंट नाम / पहचान', 'Account Name / Identifier')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.accountName}
              onChange={(e) => updateField('accountName', e.target.value)}
              placeholder={t('जैसे: School Official Page', 'e.g., School Official Page')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
              data-testid="connection-account-name"
            />
          </div>
          
          {/* Dynamic Fields based on platform */}
          {info.fields.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t(field.labelHi, field.label)} {field.required && <span className="text-red-500">*</span>}
              </label>
              <input
                type={field.type || 'text'}
                value={formData[field.key]}
                onChange={(e) => updateField(field.key, e.target.value)}
                placeholder={field.placeholder || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                required={field.required}
                data-testid={`connection-${field.key}`}
              />
            </div>
          ))}
          
          {/* Help Section */}
          {info.helpText && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700 mb-2">
                💡 {t(info.helpText, info.helpText)}
              </p>
              {info.helpUrl && (
                <a 
                  href={info.helpUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {t('Developer Portal खोलें →', 'Open Developer Portal →')}
                </a>
              )}
            </div>
          )}
          
          {/* Security Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
            🔒 {t('आपके credentials encrypted रूप में सुरक्षित store किए जाएंगे।', 
               'Your credentials will be stored securely in encrypted form.')}
          </div>
          
          <button
            type="submit"
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 font-medium"
            data-testid="connection-submit-btn"
          >
            {t('कनेक्ट करें', 'Connect')} {t(info.name_hi, info.name)}
          </button>
        </form>
      </div>
    </div>
  );
};

// Content Ideas Panel Component
const ContentIdeasPanel = ({ schoolId }) => {
  const { t, language } = useLanguage();
  const [ideas, setIdeas] = useState([]);
  const [gifs, setGifs] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchContentIdeas();
    fetchGifSuggestions();
  }, []);
  
  const fetchContentIdeas = async () => {
    try {
      const month = new Date().getMonth() + 1;
      const res = await fetch(`${API_URL}/api/social-media/content/ideas?month=${month}`);
      const data = await res.json();
      if (data.success) {
        setIdeas(data.ideas);
      }
    } catch (error) {
      console.error('Fetch ideas error:', error);
    }
    setLoading(false);
  };
  
  const fetchGifSuggestions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/content/gifs`);
      const data = await res.json();
      if (data.success) {
        setGifs(data.gifs);
      }
    } catch (error) {
      console.error('Fetch gifs error:', error);
    }
  };
  
  const getTypeColor = (type) => {
    const colors = {
      festival: 'bg-orange-100 text-orange-700',
      event: 'bg-blue-100 text-blue-700',
      national: 'bg-green-100 text-green-700',
      educational: 'bg-purple-100 text-purple-700',
      promotion: 'bg-pink-100 text-pink-700',
      awareness: 'bg-teal-100 text-teal-700',
      greeting: 'bg-yellow-100 text-yellow-700',
      motivational: 'bg-indigo-100 text-indigo-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };
  
  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="content-ideas-panel">
      {/* Monthly Content Ideas */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {t('इस महीने के कंटेंट आइडियाज', 'This Month\'s Content Ideas')}
        </h3>
        
        {ideas.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ideas.map((idea, idx) => (
              <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-800">
                    {language === 'hi' ? idea.title_hi : idea.title}
                  </h4>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getTypeColor(idea.type)}`}>
                    {idea.type}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {idea.hashtags?.slice(0, 3).map((tag, i) => (
                    <span key={i} className="text-xs text-purple-600">{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            {t('कोई आइडिया नहीं मिला', 'No ideas found')}
          </p>
        )}
      </div>
      
      {/* GIF Suggestions */}
      {gifs && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-2xl">🎬</span>
            {t('GIF सुझाव', 'GIF Suggestions')}
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gifs.categories?.map((cat, idx) => (
              <div key={idx} className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                <p className="font-medium text-gray-800">
                  {language === 'hi' ? cat.name_hi : cat.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t('खोजें:', 'Search:')} {cat.search_terms?.slice(0, 2).join(', ')}
                </p>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-gray-500 mt-4 text-center">
            💡 {t('GIPHY या Tenor पर इन keywords को खोजें', 'Search these keywords on GIPHY or Tenor')}
          </p>
        </div>
      )}
    </div>
  );
};

// Quotes Panel Component
const QuotesPanel = () => {
  const { t, language } = useLanguage();
  const [quotes, setQuotes] = useState([]);
  const [category, setCategory] = useState('education');
  const [loading, setLoading] = useState(true);
  const [copiedIdx, setCopiedIdx] = useState(null);
  
  const categories = [
    { id: 'education', name: t('शिक्षा', 'Education'), icon: '📚' },
    { id: 'motivation', name: t('प्रेरणा', 'Motivation'), icon: '💪' },
    { id: 'teachers', name: t('शिक्षक', 'Teachers'), icon: '👨‍🏫' }
  ];
  
  useEffect(() => {
    fetchQuotes();
  }, [category, language]);
  
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const lang = language === 'hi' ? 'hindi' : 'english';
      const res = await fetch(`${API_URL}/api/social-media/content/quotes?category=${category}&language=${lang}&count=10`);
      const data = await res.json();
      if (data.success) {
        setQuotes(data.quotes);
      }
    } catch (error) {
      console.error('Fetch quotes error:', error);
    }
    setLoading(false);
  };
  
  const copyQuote = (quote, idx) => {
    const text = `"${quote.quote}"\n— ${quote.author}`;
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="quotes-panel">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {t('प्रेरणादायक उद्धरण', 'Inspirational Quotes')}
      </h3>
      
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              category === cat.id
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span>{cat.icon}</span>
            {cat.name}
          </button>
        ))}
      </div>
      
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote, idx) => (
            <div 
              key={idx}
              className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100 relative group"
            >
              <p className="text-gray-800 italic text-lg">"{quote.quote}"</p>
              <p className="text-purple-600 font-medium mt-2">— {quote.author}</p>
              
              <button
                onClick={() => copyQuote(quote, idx)}
                className="absolute top-2 right-2 p-2 bg-white rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                title={t('कॉपी करें', 'Copy')}
              >
                {copiedIdx === idx ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Social Inbox Panel Component
const SocialInboxPanel = ({ schoolId }) => {
  const { t } = useLanguage();
  const [inbox, setInbox] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    fetchInbox();
  }, [schoolId, filter]);
  
  const fetchInbox = async () => {
    try {
      let url = `${API_URL}/api/social-media/school/${schoolId}/inbox`;
      if (filter !== 'all') {
        url += `?platform=${filter}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setInbox(data.inbox);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Fetch inbox error:', error);
    }
    setLoading(false);
  };
  
  const getSentimentColor = (sentiment) => {
    const colors = {
      positive: 'bg-green-100 text-green-700',
      neutral: 'bg-gray-100 text-gray-700',
      negative: 'bg-red-100 text-red-700'
    };
    return colors[sentiment] || colors.neutral;
  };
  
  const getPlatformIcon = (platform) => {
    const IconComponent = PlatformIcons[platform];
    return IconComponent ? <IconComponent /> : null;
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="social-inbox-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          {t('सोशल इनबॉक्स', 'Social Inbox')}
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{unreadCount}</span>
          )}
        </h3>
        
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          <option value="all">{t('सभी', 'All')}</option>
          <option value="facebook">Facebook</option>
          <option value="twitter">Twitter</option>
          <option value="instagram">Instagram</option>
          <option value="linkedin">LinkedIn</option>
        </select>
      </div>
      
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : inbox.length > 0 ? (
        <div className="space-y-4">
          {inbox.map((msg) => (
            <div key={msg.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-gray-400">{getPlatformIcon(msg.platform)}</span>
                  <div>
                    <p className="font-medium text-gray-800">{msg.from_name}</p>
                    <p className="text-xs text-gray-500">{msg.from_handle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getSentimentColor(msg.sentiment)}`}>
                    {msg.sentiment}
                  </span>
                  <span className="text-xs text-gray-400">{msg.type}</span>
                </div>
              </div>
              
              <p className="text-gray-700 mb-2">{msg.content}</p>
              
              {msg.post_preview && (
                <p className="text-sm text-gray-500 bg-gray-50 p-2 rounded italic">
                  {t('पोस्ट पर:', 'On post:')} "{msg.post_preview}"
                </p>
              )}
              
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-400">
                  {new Date(msg.timestamp).toLocaleString('hi-IN')}
                </span>
                <button className="text-sm text-purple-600 hover:text-purple-700">
                  {t('जवाब दें', 'Reply')}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          {t('कोई मैसेज नहीं', 'No messages')}
        </div>
      )}
    </div>
  );
};

// Main Social Media Page Component
export default function SocialMediaPage({ user, token }) {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('compose');
  const [platforms, setPlatforms] = useState([]);
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [connectionModal, setConnectionModal] = useState({ isOpen: false, platform: null });
  
  const schoolId = user?.school_id || 'default';
  
  // Fetch data
  useEffect(() => {
    fetchPlatforms();
    fetchPosts();
    fetchAnalytics();
    fetchTemplates();
  }, [schoolId]);
  
  const fetchPlatforms = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/connections`);
      const data = await res.json();
      if (data.success) {
        setPlatforms(data.platforms);
      }
    } catch (error) {
      console.error('Fetch platforms error:', error);
    }
    setLoading(false);
  };
  
  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/posts?limit=20`);
      const data = await res.json();
      if (data.success) {
        setPosts(data.posts);
      }
    } catch (error) {
      console.error('Fetch posts error:', error);
    }
  };
  
  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/analytics/overview?days=30`);
      const data = await res.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Fetch analytics error:', error);
    }
  };
  
  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/templates`);
      const data = await res.json();
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Fetch templates error:', error);
    }
  };
  
  const handleTogglePlatform = async (platform, isEnabled) => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, is_enabled: isEnabled })
      });
      const data = await res.json();
      if (data.success) {
        setPlatforms(platforms.map(p => 
          p.platform === platform ? { ...p, is_enabled: isEnabled } : p
        ));
        setToast({ message: data.message_hi || data.message, type: 'success' });
      }
    } catch (error) {
      setToast({ message: t('कुछ गलत हुआ', 'Something went wrong'), type: 'error' });
    }
  };
  
  const handleConnectPlatform = (platform) => {
    setConnectionModal({ isOpen: true, platform });
  };
  
  const handleConnectionSubmit = async (connectionData) => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(connectionData)
      });
      const data = await res.json();
      if (data.success) {
        fetchPlatforms();
        setToast({ message: data.message_hi || data.message, type: 'success' });
      }
    } catch (error) {
      setToast({ message: t('कनेक्शन विफल', 'Connection failed'), type: 'error' });
    }
  };
  
  const handleDisconnectPlatform = async (platform) => {
    if (!window.confirm(t('क्या आप वाकई डिस्कनेक्ट करना चाहते हैं?', 'Are you sure you want to disconnect?'))) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/disconnect/${platform}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        fetchPlatforms();
        setToast({ message: data.message_hi || data.message, type: 'success' });
      }
    } catch (error) {
      setToast({ message: t('डिस्कनेक्ट विफल', 'Disconnect failed'), type: 'error' });
    }
  };
  
  const handleCreatePost = async (postData) => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/posts/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData)
      });
      const data = await res.json();
      if (data.success) {
        fetchPosts();
        fetchAnalytics();
        setToast({ message: data.message_hi || data.message, type: 'success' });
      }
    } catch (error) {
      setToast({ message: t('पोस्ट बनाने में विफल', 'Failed to create post'), type: 'error' });
    }
  };
  
  const handlePublishPost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/posts/${postId}/publish`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        fetchPosts();
        fetchAnalytics();
        setToast({ message: data.message_hi || data.message, type: 'success' });
      }
    } catch (error) {
      setToast({ message: t('प्रकाशित करने में विफल', 'Failed to publish'), type: 'error' });
    }
  };
  
  const handleDeletePost = async (postId) => {
    if (!window.confirm(t('क्या आप वाकई हटाना चाहते हैं?', 'Are you sure you want to delete?'))) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/social-media/school/${schoolId}/posts/${postId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        fetchPosts();
        fetchAnalytics();
        setToast({ message: data.message_hi || data.message, type: 'success' });
      }
    } catch (error) {
      setToast({ message: t('हटाने में विफल', 'Failed to delete'), type: 'error' });
    }
  };
  
  const handleUseTemplate = (template) => {
    setActiveTab('compose');
    // Template content will be handled in composer
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="social-media-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {t('सोशल मीडिया प्रबंधन', 'Social Media Management')}
          </h1>
          <p className="text-gray-500">
            {t('एक जगह से सभी प्लेटफॉर्म पर पोस्ट करें', 'Post to all platforms from one place')}
          </p>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        <TabButton
          active={activeTab === 'compose'}
          onClick={() => setActiveTab('compose')}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>}
        >
          {t('पोस्ट बनाएं', 'Compose')}
        </TabButton>
        <TabButton
          active={activeTab === 'posts'}
          onClick={() => setActiveTab('posts')}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>}
        >
          {t('पोस्ट', 'Posts')}
        </TabButton>
        <TabButton
          active={activeTab === 'platforms'}
          onClick={() => setActiveTab('platforms')}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>}
        >
          {t('प्लेटफॉर्म', 'Platforms')}
        </TabButton>
        <TabButton
          active={activeTab === 'analytics'}
          onClick={() => setActiveTab('analytics')}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>}
        >
          {t('विश्लेषण', 'Analytics')}
        </TabButton>
        <TabButton
          active={activeTab === 'templates'}
          onClick={() => setActiveTab('templates')}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>}
        >
          {t('टेम्पलेट', 'Templates')}
        </TabButton>
        <TabButton
          active={activeTab === 'ideas'}
          onClick={() => setActiveTab('ideas')}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>}
        >
          {t('आइडियाज', 'Ideas')}
        </TabButton>
        <TabButton
          active={activeTab === 'quotes'}
          onClick={() => setActiveTab('quotes')}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>}
        >
          {t('कोट्स', 'Quotes')}
        </TabButton>
        <TabButton
          active={activeTab === 'inbox'}
          onClick={() => setActiveTab('inbox')}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>}
        >
          {t('इनबॉक्स', 'Inbox')}
        </TabButton>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'compose' && (
        <PostComposer 
          platforms={platforms} 
          onPost={handleCreatePost}
          schoolId={schoolId}
        />
      )}
      
      {activeTab === 'posts' && (
        <PostsList 
          posts={posts}
          onPublish={handlePublishPost}
          onDelete={handleDeletePost}
        />
      )}
      
      {activeTab === 'platforms' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-700">
              {t('अपने सोशल मीडिया अकाउंट को कनेक्ट करें और On/Off टॉगल से नियंत्रित करें। जिस प्लेटफॉर्म की API आपके पास है, उसे कनेक्ट करें।',
                 'Connect your social media accounts and control them with On/Off toggle. Connect platforms for which you have APIs.')}
            </p>
          </div>
          {platforms.map(platform => (
            <PlatformCard
              key={platform.platform}
              platform={platform}
              onToggle={handleTogglePlatform}
              onConnect={handleConnectPlatform}
              onDisconnect={handleDisconnectPlatform}
            />
          ))}
        </div>
      )}
      
      {activeTab === 'analytics' && (
        <AnalyticsOverview analytics={analytics} />
      )}
      
      {activeTab === 'templates' && (
        <TemplatesPanel 
          templates={templates}
          onUseTemplate={handleUseTemplate}
        />
      )}
      
      {activeTab === 'ideas' && (
        <ContentIdeasPanel schoolId={schoolId} />
      )}
      
      {activeTab === 'quotes' && (
        <QuotesPanel />
      )}
      
      {activeTab === 'inbox' && (
        <SocialInboxPanel schoolId={schoolId} />
      )}
      
      {/* Connection Modal */}
      <ConnectionModal
        isOpen={connectionModal.isOpen}
        onClose={() => setConnectionModal({ isOpen: false, platform: null })}
        platform={connectionModal.platform}
        onSubmit={handleConnectionSubmit}
      />
      
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
          <button 
            onClick={() => setToast(null)} 
            className="ml-4 text-white/80 hover:text-white"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
